import { ReactNode } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import { textSizes } from '@/theme/size-tokens';

export type LinkProps = {
  children: ReactNode;
  href: string;

  /**
   * Whether this is an external link (opens in new tab with icon)
   * @default false
   */
  external?: boolean;

  /**
   * Open in a new tab without external-link icon
   * @default false
   */
  newTab?: boolean;

  /**
   * Visual variant
   * @default 'default'
   */
  variant?: 'default' | 'muted' | 'teal' | 'inherit' | 'info';

  /**
   * When to show underline
   * @default 'hover'
   */
  underline?: 'hover' | 'always' | 'none';

  /**
   * Text size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Display behavior
   * @default 'inline'
   */
  display?: 'inline' | 'block';

  /**
   * Additional CSS classes (use as last resort)
   */
  className?: string;
};

/**
 * Link - Themed anchor with hover state
 * Automatically uses React Router for internal links
 */
function Link({
  children,
  href,
  external = false,
  newTab = false,
  variant = 'default',
  underline = 'hover',
  size = 'md',
  display = 'inline',
  className = '',
}: Readonly<LinkProps>) {
  // Build variant classes
  const variantClasses = {
    default: 'text-primary hover:text-primary-hover',
    muted: 'text-muted-foreground hover:text-foreground-hover',
    teal: 'text-teal hover:text-teal-hover',
    inherit: 'text-inherit hover:text-inherit-hover',
    info: 'text-info hover:text-info-hover',
  };

  // Build underline classes
  const underlineClasses = {
    hover: 'hover:underline',
    always: 'underline',
    none: 'no-underline',
  };

  // Build size classes from unified system
  const sizeClasses = {
    sm: textSizes.sm,
    md: textSizes.base,
    lg: textSizes.lg,
  };

  // Build display classes
  const displayClasses = {
    inline: 'inline',
    block: 'block',
  };

  const linkClassName = [
    variantClasses[variant],
    underlineClasses[underline],
    sizeClasses[size],
    displayClasses[display],
    'cursor-pointer transition-colors',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // External links use regular anchor tag
  if (external) {
    return (
      <a
        href={href}
        className={linkClassName}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
        <svg
          className="ml-1 inline-block h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
          />
        </svg>
      </a>
    );
  }

  // Internal links use React Router Link
  return (
    <RouterLink
      to={href}
      className={linkClassName}
      {...(newTab && { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {children}
    </RouterLink>
  );
}

Link.displayName = 'Link';

export default Link;
